import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, Music, X } from 'lucide-react';
import { AmbientPlayerProps } from '@/types';
import { useAmbientPlayer } from '@/hooks/useAmbientPlayer';
import { AMBIENT_SOUNDS } from '@/config/ambient';
import clsx from 'clsx';

export const AmbientPlayer: React.FC<AmbientPlayerProps> = ({ className }) => {
  const { state, actions } = useAmbientPlayer();

  const handleSoundSelect = (sound: typeof AMBIENT_SOUNDS[0]) => {
    // 如果点击的是当前正在播放的音频，则停止
    if (state.currentSound?.id === sound.id && state.isPlaying) {
      actions.stop();
    } else {
      // 否则播放新音频
      actions.play(sound);
    }
  };

  const toggleMute = () => {
    if (state.isPlaying) {
      actions.stop();
    } else if (state.currentSound) {
      actions.play(state.currentSound);
    }
  };

  return (
    <div className={clsx('fixed bottom-4 right-4 z-50', className)}>
      <AnimatePresence>
        {state.isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.2 }}
            className="mb-3 bg-black/20 backdrop-blur-xl rounded-2xl p-4 border border-white/10"
          >
            {/* 关闭按钮 */}
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-white font-medium text-sm">环境音</h3>
              <button
                onClick={actions.toggleExpanded}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* 声音选择 */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {AMBIENT_SOUNDS.map((sound) => (
                <button
                  key={sound.id}
                  onClick={() => handleSoundSelect(sound)}
                  className={clsx(
                    'p-3 rounded-xl border transition-all duration-200 text-center',
                    'hover:scale-105 active:scale-95',
                    state.currentSound?.id === sound.id && state.isPlaying
                      ? 'bg-white/20 border-white/30 text-white'
                      : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10'
                  )}
                >
                  <div className="text-lg mb-1">{sound.icon}</div>
                  <div className="text-xs">{sound.name}</div>
                </button>
              ))}
            </div>

            {/* 音量控制 */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleMute}
                className="text-white/60 hover:text-white transition-colors"
              >
                {state.isPlaying ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
              <div className="flex-1">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={state.volume}
                  onChange={(e) => actions.setVolume(parseFloat(e.target.value))}
                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
              <span className="text-white/60 text-xs w-8 text-right">
                {Math.round(state.volume * 100)}%
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 主按钮 */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={actions.toggleExpanded}
        className={clsx(
          'w-12 h-12 rounded-full border border-white/20 backdrop-blur-xl',
          'flex items-center justify-center transition-all duration-200',
          'hover:border-white/30',
          state.isPlaying
            ? 'bg-white/20 text-white'
            : 'bg-black/20 text-white/60 hover:text-white'
        )}
      >
        <Music size={20} />
        {state.isPlaying && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full"
          />
        )}
      </motion.button>

      {/* 自定义滑块样式 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .slider::-webkit-slider-thumb {
            appearance: none;
            width: 16px;
            height: 16px;
            background: white;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          }

          .slider::-moz-range-thumb {
            width: 16px;
            height: 16px;
            background: white;
            border-radius: 50%;
            border: none;
            cursor: pointer;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          }
        `
      }} />
    </div>
  );
};